import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Save, ListTodo, AlertCircle, CalendarDays, User } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TASK_TYPES = { bug: "Bug", feature: "Feature", support: "Supporto", commercial: "Commerciale" };
export const TASK_STATUSES = { todo: "Da fare", in_progress: "In corso", done: "Fatto" };
const TYPE_CLS = {
  bug: "bg-red-50 text-red-700 border-red-200",
  feature: "bg-violet-50 text-violet-700 border-violet-200",
  support: "bg-sky-50 text-sky-700 border-sky-200",
  commercial: "bg-emerald-50 text-emerald-700 border-emerald-200",
};
const STATUS_CLS = {
  todo: "bg-slate-100 text-slate-700",
  in_progress: "bg-amber-100 text-amber-800",
  done: "bg-emerald-100 text-emerald-800",
};
const SEL = "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm";

export const localDate = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const todayStr = () => localDate();
export const isOverdue = (t) => t.status !== "done" && t.due_date && t.due_date < todayStr();
export const isDueSoon = (t) => {
  if (t.status === "done" || !t.due_date || isOverdue(t)) return false;
  return (new Date(t.due_date) - new Date(todayStr())) / 86400000 <= 3;
};

export const useCsmUsers = () => {
  const [users, setUsers] = useState([]);
  useEffect(() => {
    axios.get(`${API}/admin/users`).then(r => setUsers((r.data.users || []).filter(u => ["admin", "csm"].includes(u.role)))).catch(() => {});
  }, []);
  return users;
};

const EMPTY = { title: "", type: "support", client_id: "", due_date: "", status: "todo", assignee_id: "", description: "", email_id: "", send_to_tracker: false };

export const TaskDialog = ({ open, onClose, initial, clients, onSaved }) => {
  const users = useCsmUsers();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const editing = !!initial?.task_id;

  useEffect(() => { if (open) setForm({ ...EMPTY, ...(initial || {}) }); }, [open, initial]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const trackable = ["bug", "feature"].includes(form.type);

  const save = async () => {
    if (!form.title.trim()) { toast.error("Titolo obbligatorio"); return; }
    setSaving(true);
    try {
      const body = { title: form.title, type: form.type, client_id: form.client_id || "", due_date: form.due_date || "", status: form.status, assignee_id: form.assignee_id || "", description: form.description || "" };
      if (editing) await axios.put(`${API}/csm/tasks/${initial.task_id}`, body);
      else await axios.post(`${API}/csm/tasks`, { ...body, email_id: form.email_id || "", send_to_tracker: trackable && form.send_to_tracker });
      toast.success(editing ? "Task aggiornato" : (trackable && form.send_to_tracker ? "Task creato e inviato al Tracker" : "Task creato"));
      onSaved && onSaved();
      onClose(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="task-dialog">
        <DialogHeader><DialogTitle>{editing ? "Modifica Task" : "Nuovo Task"}</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-1">
          <div className="space-y-1">
            <Label className="text-xs">Titolo</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="task-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Tipo</Label>
              <select className={SEL} value={form.type} onChange={(e) => set("type", e.target.value)} data-testid="task-type-select">
                {Object.entries(TASK_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Stato</Label>
              <select className={SEL} value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="task-status-select">
                {Object.entries(TASK_STATUSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Cliente</Label>
              <select className={SEL} value={form.client_id || ""} onChange={(e) => set("client_id", e.target.value)} data-testid="task-client-select">
                <option value="">— Nessuno —</option>
                {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Scadenza</Label>
              <Input type="date" className="h-9" value={form.due_date || ""} onChange={(e) => set("due_date", e.target.value)} data-testid="task-due-input" />
            </div>
            <div className="space-y-1 col-span-2">
              <Label className="text-xs">Assegnatario</Label>
              <select className={SEL} value={form.assignee_id || ""} onChange={(e) => set("assignee_id", e.target.value)} data-testid="task-assignee-select">
                <option value="">— Non assegnato —</option>
                {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name ? `${u.name} (${u.email})` : u.email}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Descrizione</Label>
            <textarea className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="task-description-input" />
          </div>
          {!editing && trackable && (
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="task-send-tracker-label">
              <input type="checkbox" checked={form.send_to_tracker} onChange={(e) => set("send_to_tracker", e.target.checked)} data-testid="task-send-tracker-checkbox" />
              Invia anche al Tracker
            </label>
          )}
          {editing && initial.tracker_item_id && <p className="text-xs text-muted-foreground" data-testid="task-tracker-linked">Collegato al Tracker</p>}
          <div className="flex gap-2 pt-1">
            <Button onClick={save} disabled={saving} className="gap-2" data-testid="task-save-btn"><Save className="h-4 w-4" />{saving ? "Salvataggio..." : "Salva"}</Button>
            <Button variant="outline" onClick={() => onClose(false)}>Annulla</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const TaskRow = ({ t, onEdit, onStatus, onDelete }) => (
  <tr className={`border-t border-border/40 hover:bg-muted/20 cursor-pointer ${isOverdue(t) ? "bg-red-50/60" : ""}`} onClick={() => onEdit(t)} data-testid={`task-row-${t.task_id}`}>
    <td className="px-4 py-3">
      <p className={`font-medium ${t.status === "done" ? "line-through text-muted-foreground" : ""}`}>{t.title}</p>
      {t.tracker_item_id && <span className="text-[10px] text-muted-foreground">Tracker</span>}
    </td>
    <td className="px-3 py-3"><span className={`text-xs px-2 py-0.5 rounded-full border ${TYPE_CLS[t.type]}`}>{TASK_TYPES[t.type]}</span></td>
    <td className="px-3 py-3 text-xs">{t.client_name || "—"}</td>
    <td className="px-3 py-3 text-xs">
      {t.due_date ? (
        <span className={`inline-flex items-center gap-1 ${isOverdue(t) ? "text-red-600 font-semibold" : isDueSoon(t) ? "text-orange-600 font-medium" : ""}`} data-testid={isOverdue(t) ? "task-overdue-badge" : undefined}>
          {isOverdue(t) && <AlertCircle className="h-3 w-3" />}{new Date(t.due_date).toLocaleDateString("it-IT")}
        </span>
      ) : "—"}
    </td>
    <td className="px-3 py-3 text-xs">{t.assignee_name || "—"}</td>
    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
      <select className={`h-7 rounded-full px-2 text-xs font-medium border-0 ${STATUS_CLS[t.status]}`} value={t.status} onChange={(e) => onStatus(t, e.target.value)} data-testid={`task-status-inline-${t.task_id}`}>
        {Object.entries(TASK_STATUSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
    </td>
    <td className="px-2 py-3" onClick={(e) => e.stopPropagation()}>
      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={() => onDelete(t)} data-testid={`task-delete-${t.task_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
    </td>
  </tr>
);

export const TasksView = ({ clients }) => {
  const users = useCsmUsers();
  const [tasks, setTasks] = useState([]);
  const [filters, setFilters] = useState({ status: "", type: "", client_id: "", assignee_id: "" });
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  const [dialog, setDialog] = useState({ open: false, task: null });

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/csm/tasks`, { params: filters });
      setTasks(r.data.tasks || []);
    } catch { toast.error("Errore caricamento task"); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const changeStatus = async (t, status) => {
    try { await axios.put(`${API}/csm/tasks/${t.task_id}`, { status }); load(); } catch { toast.error("Errore"); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Eliminare il task "${t.title}"?`)) return;
    try { await axios.delete(`${API}/csm/tasks/${t.task_id}`); toast.success("Task eliminato"); load(); } catch { toast.error("Errore"); }
  };

  const overdueCount = tasks.filter(isOverdue).length;
  const shown = onlyOverdue ? tasks.filter(isOverdue) : tasks;

  return (
    <div className="space-y-4" data-testid="csm-tasks">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <select className="h-9 rounded-md border border-input bg-background px-3 text-sm" value={filters.status} onChange={(e) => setF("status", e.target.value)} data-testid="tasks-filter-status">
            <option value="">Tutti gli stati</option>
            {Object.entries(TASK_STATUSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select className="h-9 rounded-md border border-input bg-background px-3 text-sm" value={filters.type} onChange={(e) => setF("type", e.target.value)} data-testid="tasks-filter-type">
            <option value="">Tutti i tipi</option>
            {Object.entries(TASK_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select className="h-9 rounded-md border border-input bg-background px-3 text-sm max-w-[200px]" value={filters.client_id} onChange={(e) => setF("client_id", e.target.value)} data-testid="tasks-filter-client">
            <option value="">Tutti i clienti</option>
            {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
          </select>
          <select className="h-9 rounded-md border border-input bg-background px-3 text-sm" value={filters.assignee_id} onChange={(e) => setF("assignee_id", e.target.value)} data-testid="tasks-filter-assignee">
            <option value="">Tutti gli assegnatari</option>
            {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name ? `${u.name} (${u.email})` : u.email}</option>)}
          </select>
          <button onClick={() => setOnlyOverdue(!onlyOverdue)} data-testid="tasks-filter-overdue"
            className={`h-9 px-3 rounded-md text-sm border transition-colors ${onlyOverdue ? "bg-red-600 text-white border-red-600" : "border-red-200 text-red-700 hover:bg-red-50"}`}>
            Scaduti ({overdueCount})
          </button>
        </div>
        <Button className="h-9 gap-2" onClick={() => setDialog({ open: true, task: null })} data-testid="task-new-btn"><Plus className="h-4 w-4" /> Nuovo Task</Button>
      </div>
      <div className="rounded-lg border border-border/60 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/30 text-xs text-muted-foreground">
              <th className="px-4 py-2.5 text-left font-medium">TASK</th>
              <th className="px-3 py-2.5 text-left font-medium">TIPO</th>
              <th className="px-3 py-2.5 text-left font-medium">CLIENTE</th>
              <th className="px-3 py-2.5 text-left font-medium"><CalendarDays className="h-3 w-3 inline mr-1" />SCADENZA</th>
              <th className="px-3 py-2.5 text-left font-medium"><User className="h-3 w-3 inline mr-1" />ASSEGNATARIO</th>
              <th className="px-3 py-2.5 text-left font-medium">STATO</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {shown.map(t => <TaskRow key={t.task_id} t={t} onEdit={(task) => setDialog({ open: true, task })} onStatus={changeStatus} onDelete={remove} />)}
          </tbody>
        </table>
        {shown.length === 0 && <p className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid="tasks-empty"><ListTodo className="h-5 w-5 mx-auto mb-1 opacity-50" />Nessun task</p>}
        <div className="px-4 py-2 bg-muted/30 border-t border-border/40 text-xs text-muted-foreground" data-testid="tasks-count">{shown.length} task</div>
      </div>
      <TaskDialog open={dialog.open} onClose={(v) => setDialog(d => ({ ...d, open: v }))} initial={dialog.task} clients={clients} onSaved={load} />
    </div>
  );
};
